import JsonSchema, { JsonSchemaComplexObject, RegularJsonSchema } from '@evidentid/json-schema/interfaces/JsonSchema';

/**
 * Yes/No criteria -- Fungus Exclusion, Coinsurance, Domestic Terrorism Excluded and the rest --
 * declare `type: boolean` for their plain value, so that a client's Boolean custom properties are
 * the ones offered when the value is taken from an entity field instead. Their values still reach
 * us as the "YES"/"NO" text that the backend's Yes/No enum encodes to, both in a template's default
 * and in configurations saved before that text was converted.
 *
 * Left alone, that text is invalid against the criterion's own schema, which puts the reference on
 * the risk profile's invalid list and blocks the save button. It is also unsafe to display: the
 * boolean control reads any non-empty string as truthy, so a "NO" shows as Yes.
 */
const YES_NO_TEXT: Record<string, boolean> = { yes: true, no: false };

/** The type declared for the value an operator actually edits.
 *
 * A reference whose value can come from a custom property wraps it in an `isBasedOnInsuredField`
 * conditional, and keeps the static alternative's schema under `else.properties.value` -- so the
 * declared type is one level in, not on the root.
 */
function plainValueType(schema: JsonSchema): unknown {
    const wrapped = (schema as JsonSchemaComplexObject).else?.properties?.value;
    return ((wrapped ?? schema) as RegularJsonSchema)?.type;
}

function castText(value: unknown): unknown {
    // Anything else is passed through rather than guessed at, so an unexpected value stays visible
    // as the validation error it is.
    return typeof value === 'string' && value.trim().toLowerCase() in YES_NO_TEXT
        ? YES_NO_TEXT[value.trim().toLowerCase()]
        : value;
}

/** Converts a Yes/No reference value to the boolean its schema declares, wrapped or not. */
export function castYesNoTextToBoolean(value: any, schema?: JsonSchema | null): any {
    if (!schema || plainValueType(schema) !== 'boolean') {
        return value;
    }
    const isWrapped = Boolean((schema as JsonSchemaComplexObject).else?.properties?.value);
    if (!isWrapped) {
        return castText(value);
    }
    // A wrapper naming a custom property carries no static value to cast.
    if (!value || typeof value !== 'object' || !('value' in value)) {
        return value;
    }
    return { ...value, value: castText(value.value) };
}
